import { isValidElement, useState } from 'react';
import toast from 'react-hot-toast';

import { cn, io } from 'lib/helpers/utility';

import Avatar from 'components/shared/Avatar/Avatar';
import Icon from 'components/shared/Icon/Icon';

import Actions from './components/Actions';

/**
 * @param {import('./lib/propTypes').NotificationProps} props
 */

const Notification = ({
  icon,
  iconColor = 'neutral',
  avatar,
  title,
  description,
  actions,
  handleClose,
  className
}) => {
  const [actionLoadingStates, setActionLoadingStates] = useState({});

  const handleActionClick = async (
    onClick = () => null,
    actionType = 'primary',
    closeOnClick = false
  ) => {
    try {
      setActionLoadingStates((prev) => ({ ...prev, [actionType]: true }));
      await onClick();
      setActionLoadingStates((prev) => ({ ...prev, [actionType]: false }));
      if (closeOnClick) handleClose?.();
    } catch (error) {
      console.error('Notification action error:', error);
      setActionLoadingStates((prev) => ({ ...prev, [actionType]: false }));
    }
  };

  const hasAvatar = io(avatar);
  const titleIsNode = isValidElement(title);
  const descriptionIsNode = isValidElement(description);

  return (
    <div
      className={cn(
        'flex h-[60px] w-[498px] items-center gap-2 rounded-xl border border-solid border-neutral-300 bg-white p-3 shadow-[0px_0px_16px_2px_rgba(65,70,81,0.12)]',
        className
      )}>
      {hasAvatar ? (
        <div className="shrink-0">
          <Avatar size="large" {...avatar} />
        </div>
      ) : icon ? (
        <Icon icon={icon} size={28} color={iconColor} shade={iconColor === 'neutral' ? 700 : 500} />
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        {title &&
          (titleIsNode ? (
            title
          ) : (
            <span
              className="line-clamp-1 text-[14px] font-500 leading-[18px] text-neutral-800 first-letter:uppercase"
              title={typeof title === 'string' ? title : undefined}>
              {title}
            </span>
          ))}
        {description &&
          (descriptionIsNode ? (
            description
          ) : (
            <p
              className="line-clamp-1 break-words text-sm font-400 leading-[18px] text-neutral-700 first-letter:uppercase"
              title={typeof description === 'string' ? description : undefined}>
              {description}
            </p>
          ))}
      </div>

      <div className="flex h-fit shrink-0 items-center gap-2">
        <Actions
          actions={actions}
          actionLoadingStates={actionLoadingStates}
          handleActionClick={handleActionClick}
          handleClose={handleClose}
        />
        {handleClose && (
          <button
            className="cursor-pointer p-0 hover:opacity-80"
            type="button"
            aria-label="Close notification"
            onClick={handleClose}>
            <Icon icon="close-linear" color="neutral" shade={700} />
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * @param {import('./lib/propTypes').ShowNotificationProps} props
 */

export const showNotification = ({
  icon,
  avatar,
  iconColor,
  title,
  description,
  actions,
  duration = description ? 5000 : 3000
}) => {
  toast.custom(
    (t) => (
      <Notification
        icon={icon}
        avatar={avatar}
        iconColor={iconColor}
        title={title}
        description={description}
        actions={actions}
        handleClose={() => toast.remove(t?.id)}
      />
    ),
    { position: 'top-right', duration }
  );
};
